import { useQuery } from '@apollo/client'
import {
  Box,
  Form,
  Text,
  WizardStep,
  WizardStepRenderParams,
  useOnNext,
} from '@factorialco/deprecated-design-system'
import { GetEmployeesForBudgetsDocument } from 'generated/resources/graphql'

import LabeledInput from 'modules/core/components/LabeledInput'
import Selector from 'modules/core/components/Selector'
import i18n from 'modules/core/lib/i18n'

import {
  WizardData,
  WizardDataField,
  validateMandatoryFIels,
} from 'modules/finance/lib/newBudgetWizard'

const BudgetAccessStep = ({ data, onDataChanged }: WizardStepRenderParams<WizardData>) => {
  const handleNext = async () => {
    const mandatoryFields: WizardDataField[] = ['ownerEmployeeIds']

    const { invalidFields, dataWithErrors } = validateMandatoryFIels(mandatoryFields, data)

    onDataChanged(dataWithErrors)

    if (invalidFields.length) {
      return Promise.reject()
    }
  }

  const { data: employeeData } = useQuery(GetEmployeesForBudgetsDocument, {})
  const employees = employeeData?.employees.employees || []

  const options = employees.map((employee) => {
    return (
      <Selector.Option
        key={employee.id}
        value={employee.id}
        label={String(employee?.fullName)}
        searchValue={String(employee?.fullName)}
      >
        {employee?.fullName}
      </Selector.Option>
    )
  })

  useOnNext(handleNext)
  return (
    <Box gap="s40" flexDirection="column">
      <Box gap="s24" width="full">
        <Box gap="s8">
          <Text.Header2>{i18n.t('budgets.forms.new_budget.budget_access.title')}</Text.Header2>

          <Text.Normal>{i18n.t('budgets.forms.new_budget.budget_access.description')}</Text.Normal>
        </Box>
      </Box>
      <Box gap="s40" maxWidth="s600">
        <Form padding="s0" notRounded>
          <LabeledInput
            isLegend
            label={i18n.t('budgets.forms.new_budget.budget_access.budget_owners')}
            description={i18n.t('budgets.forms.new_budget.budget_access.budget_owners_description')}
          >
            <Selector.Popover
              value={data.ownerEmployeeIds}
              blankLabel=""
              placeholder={i18n.t('budgets.forms.new_budget.budget_access.select_employees')}
              onChange={(userIds) =>
                onDataChanged({ ...data, ownerEmployeeIds: userIds as number[] })
              }
              inputStyle="badge"
              mandatory
              isMultiSelect
            >
              {options}
            </Selector.Popover>
          </LabeledInput>

          <Text.Normal color="radicalRed1200">{data.errors?.ownerIds}</Text.Normal>

          <LabeledInput
            isLegend
            label={i18n.t('budgets.forms.new_budget.budget_access.budget_viewers')}
            description={i18n.t(
              'budgets.forms.new_budget.budget_access.budget_viewers_description'
            )}
          >
            <Selector.Popover
              value={data.viewerEmployeeIds}
              blankLabel=""
              placeholder={i18n.t('budgets.forms.new_budget.budget_access.select_employees')}
              onChange={(userIds) =>
                onDataChanged({ ...data, viewerEmployeeIds: userIds as number[] })
              }
              inputStyle="badge"
              isMultiSelect
            >
              {options}
            </Selector.Popover>
          </LabeledInput>

          <Text.Normal color="radicalRed1200">{data.errors?.viewerIds}</Text.Normal>
        </Form>
      </Box>
    </Box>
  )
}

export const budgetAccessStep = (): WizardStep<WizardData> => ({
  title: i18n.t('budgets.forms.new_budget.budget_access.title'),
  nextText: i18n.t('common.next'),
  render: (props) => <BudgetAccessStep {...props} />,
  isCompleted: () => true,
})
